import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { CreditCard, AlertCircle, CheckCircle2, Clock, X } from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  Card,
  Button,
  StatusBadge,
  NotificationCard,
  Modal,
  ModalBody,
  ModalFooter,
  useToast,
} from '@sighthost/ui';
import { supabase } from '@/shared/lib/supabase';
import { PaymentTrustStrip } from '@/shared/components/feedback/PaymentTrustStrip';
import { useHostingSubscriptions } from '@/domains/billing/hooks/useHostingSubscriptions';
import { useHostingSubscriptionInitiate } from '@/domains/billing/hooks/useHostingSubscriptionInitiate';
import { useHostingSubscriptionCancel } from '@/domains/billing/hooks/useHostingSubscriptionCancel';
import type { HostingSubscription } from '@/domains/billing/types/hostingSubscription.types';
import type { StatusTone } from '@sighthost/ui';

/**
 * AutoPayPage — manage hosting PayFast Recurring subscriptions.
 *
 * Spec: _bmad-output/PHASE_PF_Q3_REVERSAL_BRD_2026-06-16.md §7.1
 *
 * For each of the customer's active hosting accounts (customer_link rows),
 * either:
 *   - Show "Set up auto-pay" → kicks off PayFast Recurring form (browser
 *     auto-submits to PayFast's hosted checkout for card capture).
 *   - Show the active subscription card with status badge + next charge
 *     date + amount + "Cancel auto-pay" link.
 *
 * Customer cancel = revert to per-invoice billing. Hosting stays live. The
 * customer_link.recurring_enabled flag is flipped automatically by a DB
 * trigger after the subscription status changes (single source of truth).
 */

interface HostingAccount {
  id: string;
  cpanelUsername: string;
  primaryDomain: string | null;
  recurringEnabled: boolean;
  servicePlanName: string;
  recurringAmount: number;
  currentPeriodEnd: string | null;
}

interface CustomerLinkRow {
  id: string;
  cpanel_username: string | null;
  primary_domain: string | null;
  recurring_enabled: boolean;
  current_period_end: string | null;
  unlinked_at: string | null;
  service_plan: {
    name: string;
    recurring_amount: string | number;
  } | null;
}

function STATUS_TONE(status: HostingSubscription['status']): StatusTone {
  switch (status) {
    case 'active': return 'active';
    case 'pending': return 'pending';
    case 'at_risk': return 'overdue';
    case 'cancelled': return 'draft';
  }
}

function STATUS_LABEL(status: HostingSubscription['status']): string {
  switch (status) {
    case 'active': return 'Active';
    case 'pending': return 'Setting up';
    case 'at_risk': return 'Payment failed';
    case 'cancelled': return 'Cancelled';
  }
}

function formatZAR(amount: number): string {
  return `R${amount.toFixed(2)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-ZA', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export default function AutoPayPage() {
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<HostingAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const subs = useHostingSubscriptions();
  const cancelMut = useHostingSubscriptionCancel();
  const [cancelTarget, setCancelTarget] = useState<HostingSubscription | null>(null);

  async function loadAccounts() {
    setAccountsLoading(true);
    setAccountsError(null);
    const { data, error } = await supabase
      .from('customer_link')
      .select(
        'id, cpanel_username, primary_domain, recurring_enabled, current_period_end, unlinked_at, service_plan:service_plan_id (name, recurring_amount)'
      )
      .is('unlinked_at', null)
      .order('cpanel_username', { ascending: true });

    if (error) {
      setAccountsError(error.message);
      setAccounts([]);
    } else {
      const mapped: HostingAccount[] = ((data as unknown as CustomerLinkRow[]) ?? [])
        .filter((r) => r.service_plan && r.cpanel_username)
        .map((r) => ({
          id: r.id,
          cpanelUsername: r.cpanel_username!,
          primaryDomain: r.primary_domain,
          recurringEnabled: r.recurring_enabled,
          servicePlanName: r.service_plan!.name,
          recurringAmount: Number(r.service_plan!.recurring_amount),
          currentPeriodEnd: r.current_period_end,
        }));
      setAccounts(mapped);
    }
    setAccountsLoading(false);
  }

  useEffect(() => {
    loadAccounts();
  }, []);

  const activeSubsByLink = useMemo(() => {
    const map = new Map<string, HostingSubscription>();
    for (const s of subs.subscriptions) {
      if (s.status !== 'cancelled') {
        map.set(s.customerLinkId, s);
      }
    }
    return map;
  }, [subs.subscriptions]);

  async function handleCancel(sub: HostingSubscription) {
    const result = await cancelMut.cancel(sub.id);
    if (result) {
      toast({
        tone: 'success',
        title: 'Auto-pay cancelled',
        body:
          result.payfast_cancel_ok === false
            ? 'Local cancellation recorded. Our team will mirror it at PayFast.'
            : 'Future months return to manual invoicing. Service stays active.',
      });
      setCancelTarget(null);
      await Promise.all([subs.refresh(), loadAccounts()]);
    } else if (cancelMut.error) {
      toast({
        tone: 'danger',
        title: 'Could not cancel',
        body: cancelMut.error.message,
      });
    }
  }

  const isLoading = accountsLoading || subs.isLoading;
  const loadError = accountsError ?? subs.error;

  return (
    <UniversalPage
      title={
        <span className="inline-flex items-center gap-2">
          <CreditCard className="size-5 text-muted-foreground" />
          Auto-pay
        </span>
      }
      description="Charge your card automatically each month, instead of paying every invoice by EFT."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Billing', href: '/app/billing' },
        { label: 'Auto-pay' },
      ]}
    >
      {isLoading && <UniversalLoadingState variant="spinner" caption="Loading your hosting accounts…" />}

      {!isLoading && loadError && (
        <NotificationCard
          tone="danger"
          title="Could not load auto-pay status"
          body={loadError}
        />
      )}

      {!isLoading && !loadError && accounts.length === 0 && (
        <Card interactive={false} className="p-6">
          <h3 className="mb-2 text-base font-semibold text-ink">
            No hosting accounts found
          </h3>
          <p className="text-sm text-muted-foreground">
            You don't have any active hosting accounts on your profile yet.
            Auto-pay is for monthly hosting subscriptions.
          </p>
          <div className="mt-4">
            <Button asChild size="sm">
              <Link to="/app/services/new">Order hosting</Link>
            </Button>
          </div>
        </Card>
      )}

      {!isLoading && !loadError && accounts.length > 0 && (
        <div className="space-y-4">
          <NotificationCard
            tone="info"
            title="How auto-pay works"
            body="We securely set up a recurring charge with PayFast. We never see or store your card details. Cancel any time from this page — your hosting stays live and we'll go back to monthly invoices."
          />

          <PaymentTrustStrip />


          {accounts.map((acct) => (
            <AccountCard
              key={acct.id}
              account={acct}
              subscription={activeSubsByLink.get(acct.id) ?? null}
              onCancelClick={(sub) => setCancelTarget(sub)}
            />
          ))}
        </div>
      )}

      <Modal
        open={cancelTarget !== null}
        onOpenChange={(open) => { if (!open) setCancelTarget(null); }}
        title="Cancel auto-pay?"
      >
        <ModalBody>
          <p className="text-sm text-muted-foreground">
            Cancelling auto-pay means future months will return to manual invoicing.
            We'll keep emailing you the monthly invoice; pay by EFT or one-click PayFast as usual.
            Your hosting account stays live — nothing changes about the service.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            You can re-enable auto-pay at any time.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => setCancelTarget(null)}
            disabled={cancelMut.isLoading}
          >
            Keep auto-pay
          </Button>
          <Button
            variant="destructive"
            onClick={() => cancelTarget && handleCancel(cancelTarget)}
            loading={cancelMut.isLoading}
          >
            Cancel auto-pay
          </Button>
        </ModalFooter>
      </Modal>
    </UniversalPage>
  );
}

// ─── Account card (one per customer_link) ────────────────────────────────

interface AccountCardProps {
  account: HostingAccount;
  subscription: HostingSubscription | null;
  onCancelClick: (sub: HostingSubscription) => void;
}

function AccountCard({ account, subscription, onCancelClick }: AccountCardProps) {
  const initiateMut = useHostingSubscriptionInitiate(account.id);

  return (
    <Card interactive={false} className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3">
        <div>
          <h3 className="text-base font-semibold text-ink">
            {account.primaryDomain ?? account.cpanelUsername}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {account.servicePlanName} · {formatZAR(account.recurringAmount)} / month · cPanel: {account.cpanelUsername}
          </p>
        </div>
        {subscription && (
          <StatusBadge tone={STATUS_TONE(subscription.status)}>
            {STATUS_LABEL(subscription.status)}
          </StatusBadge>
        )}
      </div>

      {!subscription && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            Currently invoiced monthly. Set up auto-pay to charge your card automatically each month.
            {account.currentPeriodEnd && (
              <span> First charge: {formatDate(account.currentPeriodEnd)}.</span>
            )}
          </div>
          <Button
            onClick={initiateMut.initiate}
            loading={initiateMut.isLoading}
            size="sm"
          >
            <CreditCard className="size-4" />
            Set up auto-pay
          </Button>
        </div>
      )}

      {subscription && subscription.status === 'pending' && (
        <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
          <Clock className="mt-0.5 size-4 text-info" />
          <span>
            Waiting for PayFast to confirm card capture. This usually takes a few seconds.
            Refresh in a moment if the status doesn't update.
          </span>
        </div>
      )}

      {subscription && subscription.status === 'active' && (
        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <CheckCircle2 className="size-4 text-success" />
              Active — next charge {formatDate(subscription.nextBillingDate)} for{' '}
              {formatZAR(subscription.recurringAmount)}
            </div>
            {subscription.lastCycleAt && (
              <div className="text-xs text-muted-foreground">
                Last charge: {formatDate(subscription.lastCycleAt)}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCancelClick(subscription)}
          >
            <X className="size-4" />
            Cancel auto-pay
          </Button>
        </div>
      )}

      {subscription && subscription.status === 'at_risk' && (
        <div className="mt-3 space-y-3">
          <div className="flex items-start gap-2 text-sm">
            <AlertCircle className="mt-0.5 size-4 text-warning" />
            <div>
              <p className="font-medium text-ink">Last payment failed</p>
              <p className="text-muted-foreground">
                PayFast couldn't charge your card. Common causes: expired card, insufficient
                funds, or bank decline. Update your card details in the PayFast customer
                portal, or cancel auto-pay and we'll send you an invoice to pay manually.
              </p>
              {subscription.failedCycleAt && (
                <p className="mt-1 text-xs text-muted-foreground">
                  We'll auto-revert to manual invoicing on{' '}
                  {formatDate(new Date(
                    new Date(subscription.failedCycleAt).getTime() + 7 * 24 * 60 * 60 * 1000
                  ).toISOString())}{' '}
                  if the card isn't updated.
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCancelClick(subscription)}
            >
              Cancel auto-pay
            </Button>
          </div>
        </div>
      )}

      {initiateMut.error && (
        <NotificationCard
          tone="danger"
          className="mt-3"
          title="Could not start auto-pay setup"
          body={initiateMut.error.message}
        />
      )}
    </Card>
  );
}
